import React, {Fragment, useEffect} from "react";
import styled from "@emotion/styled";
import {useTranslation} from "react-i18next";
import {useIsMobile} from "data/hooks/useIsMobile";
import {Exist} from "views/components/Exist";
import {
	BurgerButton,
	Img,
	Navigation,
	NavigationList,
	NavigationWrapper,
	SearchInput,
} from "./common";
import {Item} from "./Mobile";
import {ReactComponent as BurgerIcon} from "assets/img/icons/burger.svg";
import {ReactComponent as CloseIcon} from "assets/img/icons/close.svg";
import {socialNetworkLinks} from "data/constants/socialNetworkLinks";
import {useViewController} from "data/services/locator";
import {Bindings} from "data/constants/bindings";
import {IMobileMenuController} from "./MobileMenu.controller";
import {observer} from "mobx-react";
import {PureButton} from "views/components/common/buttons/pureButton";

const Wrapper = styled.div`
	display: flex;
	width: 100%;
	justify-content: space-between;
	align-items: center;
	padding: 5px 0;

	div:last-child {
		width: 40px;
	}

	@media (min-width: 769px) {
		display: none;
	}
`;

const SubNavigation = styled.div`
	padding: 10px 30px 30px;
`;

const SubNavigationTitle = styled.h3`
	font-size: 18px;
	font-weight: 500;
	letter-spacing: 0.025em;
	text-transform: uppercase;
`;

const LogoutLink = styled(PureButton)`
	display: block;
	margin-top: 18px;
	font-size: 18px;
	font-weight: 500;
	letter-spacing: 0.025em;
	text-transform: uppercase;
	padding-left: 0;
`;

const SocialLinks = styled.div`
	display: flex;
	align-items: center;
	margin-top: 8px;

	a {
		display: flex;
		align-items: center;
		justify-content: center;
		background-color: #2b2b2b;
		width: 32px;
		height: 32px;
		border-radius: 50%;
		margin: 10px 8px 0 0;
		transition: all 0.2s ease-out;

		img {
			width: 16px;
		}

		&:hover {
			transform: scale(1.15);
			background-color: ${({theme}) => theme.background.mobileIconHover};
		}
	}
`;

const Copyright = styled.div`
	padding: 0 30px;
	color: #404040;
	font-size: 12px;
`;

export const MobileMenu: React.FC = observer(() => {
	const {t, i18n} = useTranslation();
	const {isMobile} = useIsMobile();
	const {
		isShowCoupon,
		showCouponHandler,
		isOpenMenu,
		setIsOpenMenu,
		toggleMenuHandler,
		isAuthorized,
		value,
		setValue,
		icon,
		localeUrls,
		language,
		logoLink,
		getSocialNetworkUrl,
		logoutHandler,
		navClickHandler,
		searchHandler,
	} = useViewController<IMobileMenuController>(Bindings.MobileMenuController, {
		language: i18n.language,
	});

	useEffect(() => {
		if (!isMobile && isOpenMenu) {
			setIsOpenMenu(false);
		}

		const rootElement = document.querySelector("body");

		if (rootElement) {
			rootElement.style.overflow = isOpenMenu ? "hidden" : "initial";
		}
	}, [isMobile, isOpenMenu, setIsOpenMenu]);

	return (
		<Fragment>
			<Wrapper>
				<BurgerButton onClick={toggleMenuHandler}>
					{isOpenMenu ? <CloseIcon /> : <BurgerIcon />}
				</BurgerButton>

				<a href={logoLink}>
					<Img src={icon} alt="CFL" />
				</a>

				<div />
			</Wrapper>

			<NavigationWrapper isOpen={isOpenMenu} onClick={toggleMenuHandler}>
				<Navigation isOpen={isOpenMenu} onClick={navClickHandler}>
					<form onSubmit={searchHandler}>
						<SearchInput name="search" value={value} onChange={setValue} />
					</form>

					<NavigationList>
						{localeUrls.map((el, i) => (
							<Item
								key={`${el.url}-${i}`}
								{...el}
								toggleMainMenu={toggleMenuHandler}
								showCoupon={showCouponHandler}
								isShowCoupon={isShowCoupon}
							/>
						))}
					</NavigationList>

					<SubNavigation>
						<SubNavigationTitle>{t("navigation.follow_cfl")}</SubNavigationTitle>
						<SocialLinks>
							{socialNetworkLinks.map(({url, name, icon}) => (
								<a key={name} href={getSocialNetworkUrl(url, language)}>
									<img src={icon} alt={name} />
								</a>
							))}
						</SocialLinks>

						<Exist when={isAuthorized}>
							<LogoutLink onClick={logoutHandler}>
								{t("navigation.logout")}
							</LogoutLink>
						</Exist>
					</SubNavigation>

					<Copyright>
						<p>{t("navigation.copyright")}</p>
					</Copyright>
				</Navigation>
			</NavigationWrapper>
		</Fragment>
	);
});
